/**
 * API 客户端（带 Supabase 认证）
 */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getAccessToken } from './supabaseAuth';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

const PUBLIC_PATHS = ['/login', '/register', '/forgot-password'];
const PUBLIC_API_PATTERNS = ['/api/health', '/api/public'];
const REDIRECT_COOLDOWN_MS = 5000;

let lastRedirectTime = 0;
let refreshSessionPromise: Promise<string | null> | null = null;

interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  __retryCount?: number;
}

type ApiClientError = Error & {
  response?: unknown;
  status?: number;
};

function shouldSkipAuthFlow(requestUrl: string): boolean {
  if (typeof window !== 'undefined') {
    if (PUBLIC_PATHS.some((path) => window.location.pathname.startsWith(path))) {
      return true;
    }
  }

  return PUBLIC_API_PATTERNS.some((pattern) => requestUrl.includes(pattern));
}

function shouldRedirectToLogin(): boolean {
  const now = Date.now();
  if (now - lastRedirectTime < REDIRECT_COOLDOWN_MS) {
    return false;
  }

  lastRedirectTime = now;
  return true;
}

function buildRequestUrl(config?: AxiosRequestConfig): string {
  if (!config) {
    return '';
  }

  const baseURL = config.baseURL || API_BASE_URL;
  const url = config.url || '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `${baseURL}${url}`;
}

function getCurrentReturnUrl(): string {
  if (typeof window === 'undefined') {
    return '/';
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

async function refreshAccessTokenOnce(): Promise<string | null> {
  if (!refreshSessionPromise) {
    refreshSessionPromise = (async () => {
      try {
        const { supabase } = await import('@/lib/supabase/client');
        const {
          data: { session },
          error,
        } = await supabase.auth.refreshSession();

        if (error || !session?.access_token) {
          return null;
        }

        localStorage.setItem('token', session.access_token);
        return session.access_token;
      } catch (_error) {
        return null;
      } finally {
        refreshSessionPromise = null;
      }
    })();
  }

  return refreshSessionPromise;
}

// 创建 axios 实例
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：添加认证 token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('获取 token 失败:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：处理错误并统一返回数据格式
apiClient.interceptors.response.use(
  (response) => {
    // 统一返回 response.data，保持 API 契约一致性
    return response.data;
  },
  async (error) => {
    if (error.response) {
      const status = error.response.status;
      const requestConfig = (error.config || {}) as RetryableAxiosRequestConfig;
      const requestUrl = buildRequestUrl(requestConfig);

      // 401 走渐进式恢复：单次重试 -> 软退出提示 -> 跳转登录
      if (status === 401 && !shouldSkipAuthFlow(requestUrl)) {
        if ((requestConfig.__retryCount || 0) < 1) {
          requestConfig.__retryCount = 1;
          const refreshedToken = await refreshAccessTokenOnce();

          if (refreshedToken) {
            requestConfig.headers = requestConfig.headers || {};
            (requestConfig.headers as Record<string, string>).Authorization = `Bearer ${refreshedToken}`;
            return apiClient.request(requestConfig);
          }
        }

        localStorage.removeItem('userStatus');
        if (typeof window !== 'undefined' && shouldRedirectToLogin()) {
          const returnUrl = encodeURIComponent(getCurrentReturnUrl());
          window.location.href = `/login?returnUrl=${returnUrl}`;
        }
      }

      // 提取服务端返回的语义化错误信息
      const serverError = error.response.data?.error;
      if (serverError) {
        // 创建新的 Error 对象，使用服务端的错误信息
        const customError: ApiClientError = new Error(serverError);
        // 保留原始 response 对象，便于调试
        customError.response = error.response;
        customError.status = status;
        return Promise.reject(customError);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
